import { Action } from 'redux';
import { put } from 'redux-saga/effects';
import { ACTION_CREATOR_NAME, ErrorAction } from '@sofology/sofologycomponents';

import { api } from 'repositories/api';
import { CheckoutActionTypes, CheckoutActions } from 'store/checkout';
import { BasketSagaReturnType } from 'store/sagaHelpers';
import { IlpQuestionDescriptions } from 'models';
import { errorToBasketErrorModel } from 'helpers';

export interface LoadQuestionSetAction extends Action<CheckoutActionTypes.LOAD_QUESTION_SET> {
}

export interface LoadQuestionSetActionStart extends Action<CheckoutActionTypes.LOAD_QUESTION_SET_START> {
}

export interface LoadQuestionSetActionSuccess extends Action<CheckoutActionTypes.LOAD_QUESTION_SET_SUCCESS> {
    questionSet: IlpQuestionDescriptions;
}

export interface LoadQuestionSetActionError extends ErrorAction<CheckoutActionTypes.LOAD_QUESTION_SET_ERROR> {
}

export const loadQuestionSet: () => LoadQuestionSetAction = () => ({
    type: CheckoutActionTypes.LOAD_QUESTION_SET,
});
loadQuestionSet[ACTION_CREATOR_NAME] = 'loadQuestionSet';

export type LoadQuestionSetFunc = (...args: Parameters<typeof loadQuestionSet>) => void;

export function* loadQuestionSetSaga(): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.LOAD_QUESTION_SET_START
    });

    try {
        let questionSet = yield api.ifcConfig.getIfcQuestionSet();

        temporaryModifyQuestionSet(questionSet);

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.LOAD_QUESTION_SET_SUCCESS,
            questionSet: questionSet
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.LOAD_QUESTION_SET_ERROR,
            error: errorToBasketErrorModel(error, 'loadQuestionSetSaga')
        });

        return false;
    }
}

// these changes should be added from DFS side
// but to speed up the proccess we hardcode them here
// TO-DO: remove this DIRTY HACK!!!
function temporaryModifyQuestionSet(questionSet: IlpQuestionDescriptions): void {
    let customerProperties = questionSet['customer'].properties as any;
    customerProperties = {
        ...customerProperties,
        'title': {
            ...customerProperties['title'],
            'readonly': true
        },
        'forename': {
            ...customerProperties['forename'],
            'readonly': true,
            'forbiddenCharsPattern': '(?![a-zA-Z\\-\\s]).'
        },
        'surname': {
            ...customerProperties['surname'],
            'readonly': true,
            'forbiddenCharsPattern': '(?![a-zA-Z\\-\\s]).'
        },
        'dob': {
            ...customerProperties['dob'],
            'validate': [
                {
                    'type': 'condition',
                    'expression': '${((new Date() - new Date(value))/ (1000 * 60 * 60 * 24 * 365.2422))>=18}',
                    'message': 'You must be at least 18 years of age to apply for finance'
                }
            ],
            description: 'You must be 18 years or over to apply'
        }
    };

    let bankProperties = questionSet['bank'].properties as any;
    bankProperties = {
        ...bankProperties,
        'isBankDetailsConfirmed': {
            'componentType': 'checkBox',
            'label': 'By completing your bank details you are confirming that you are the account holder of the specified UK Bank/Building Society Account and that you are the only person required to authorise debits from this account.',
            'name': 'isBankDetailsConfirmed',
            'type': 'boolean',
            'validate': [
                {
                    'type': 'requiredif',
                    'field': 'isBankDetailsConfirmed',
                    'value': false,
                    'operator': '=',
                    'message': 'Please confirm that the statement above is true'
                }
            ]
        },
        'accountHolderName': {
            ...bankProperties['accountHolderName'],
            'forbiddenCharsPattern': '(?![a-zA-Z\\-\\s]).',
        },
        'sortCode': {
            ...bankProperties['sortCode'],
            'validate': [
                {
                    'type': 'bankdata',
                    'field': 'customer.banking.accountNumber',
                    'message': 'Your account number and sort code do not match'
                }
            ],
        },
        'accountNumber': {
            ...bankProperties['accountNumber'],
            'forbiddenCharsPattern': '(?![0-9]).',
            'validate': [
                {
                    'field': 'customer.banking.sortCode'
                }
            ]
        }
    };
    // account number secription: Account Numbers are usually 8 digits. If this is correct, please proceed

    questionSet['customer'].properties = customerProperties;
    questionSet['bank'].properties = bankProperties;

    let employmentTenureProperties = questionSet['employment'].properties['employmentTenure'].properties as any;
    employmentTenureProperties = {
        ...employmentTenureProperties,
        'months': {
            ...employmentTenureProperties['months'],
            'defaultValueForFailedCondition': 0
        },
        'years': {
            ...employmentTenureProperties['years'],
            'defaultValueForFailedCondition': 0
        }
    };
    questionSet['employment'].properties['employmentTenure'].properties = employmentTenureProperties;

    let addressProperties = questionSet['address'].properties as any;
    modifyAddressProperties(addressProperties.currentAddress, 'customer.address.currentAddress');
    modifyAddressProperties(addressProperties.deliveryAddress, 'loan.otherDeliveryAddress');
    modifyAddressProperties(addressProperties.previousAddress1, 'customer.address.previousAddresses.0');
    modifyAddressProperties(addressProperties.previousAddress2, 'customer.address.previousAddresses.1');
    addressProperties = {
        ...addressProperties,
        'currentAddress': {
            ...addressProperties['currentAddress'],
            'readonly': true,
            'label': 'Your current address:',
        },
        'deliveryAddress': {
            ...addressProperties['deliveryAddress'],
            'readonly': true,
            'label': 'Your delivery address:'
        },
        'timeAtCurrentAddress': {
            ...addressProperties['timeAtCurrentAddress'],
            'label': 'Time at current address'
        },
        'timeAtPreviousAddress1': {
            ...addressProperties['timeAtPreviousAddress1'],
            'label': 'Time at this address'
        },
        'timeAtPreviousAddress2': {
            ...addressProperties['timeAtPreviousAddress2'],
            'label': 'Time at this address'
        },
        'previousAddress1': {
            ...addressProperties['previousAddress1'],
            'label': 'Previous Address 1'
        },
        'previousAddress2': {
            ...addressProperties['previousAddress2'],
            'label': 'Previous Address 2'
        },
        'differentDeliveryAddress': {
            ...addressProperties['differentDeliveryAddress'],
            'condition': {
                'expression': 'false',
                'fields': {
                }
            }
        },
        'telephone': {
            ...addressProperties['telephone'],
            'required': true,
            'minLength': 10,
            'maxLength': 11,
            'forbiddenCharsPattern': '(?![\\d ]).',
            'description': 'It\'s preferred to use mobile phone'
        }
    };

    delete addressProperties['identificationDocument'];
    delete addressProperties['identificationReference'];

    questionSet['address'].properties = addressProperties;
}

function modifyAddressProperties(addressComponent: any, propertyNamePrefix: string) {
    let addressProperties = addressComponent.properties;

    addressProperties = {
        ...addressProperties,
        'subPropertyNumber': {
            ...addressProperties['subPropertyNumber'],
            'forbiddenCharsPattern': '(?![a-zA-Z\\d,.\\-\\s&/]).',
            'minLength': undefined,
        },
        'number': {
            ...addressProperties['number'],
            'forbiddenCharsPattern': '(?![a-zA-Z\\d\\-\\s/]).',
            'minLength': undefined,
            'validate': [
                {
                    'type': 'requiredif',
                    'field': `${propertyNamePrefix}.name`,
                    'operator': '=',
                    'value': '',
                    'message': 'House name or number is requered'
                }
            ]
        },
        'name': {
            ...addressProperties['name'],
            'minLength': undefined,
            'forbiddenCharsPattern': '(?![a-zA-Z0-9,\\.\\-\\s/]).',
            'validate': [
                {
                    'type': 'requiredif',
                    'field': `${propertyNamePrefix}.number`,
                    'operator': '=',
                    'value': '',
                    'message': 'House name or number is requered'
                }
            ]
        },
        'street': {
            ...addressProperties['street'],
            'forbiddenCharsPattern': '(?![a-zA-Z0-9,\\.\\-\\s\']).',
        },
        'town': {
            ...addressProperties['town'],
            'forbiddenCharsPattern': '(?![a-zA-Z0-9,\\.\\-\\s\']).',
        },
        'county': {
            ...addressProperties['county'],
            'forbiddenCharsPattern': '(?![a-zA-Z0-9\\-\\s]).',
            'minLength': undefined,
        },
        'postCode': {
            ...addressProperties['postCode'],
            'forbiddenCharsPattern': '(?![a-zA-Z0-9\\s]).',
        }
    };

    addressComponent.properties = addressProperties;
}